import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setSelectedFileDoi } from "reducers/future-medical";
import { useGetDateOfInjuryQuery } from "services/doi-api";
import type { FileType } from "types/DataTypes";
import { useSelector } from "utils/redux";

/**
 * handle file-based DOI selection and data fetching
 * @param file File object
 */
export function useFileDOI(file?: FileType) {
  const dispatch = useDispatch();
  const selectedFileDoi = useSelector(
    (state) => state.futureMedical.selectedFileDoi,
  );
  const fileId = useSelector((state) => state.files.fileId);

  // file path queries
  const fileIdToUse = file?.id || fileId;

  const {
    data: dois,
    isLoading,
    isSuccess,
  } = useGetDateOfInjuryQuery(
    {
      file: fileIdToUse?.toString() || "",
    },
    { skip: !fileIdToUse },
  );

  // select a DOI by ID
  const selectDoi = useCallback(
    (doiId: number | string | null) => {
      if (!dois?.results) return;

      const numericId = doiId
        ? typeof doiId === "string"
          ? parseInt(doiId)
          : doiId
        : null;

      dispatch(setSelectedFileDoi(numericId));
      // we don't set currentServiceDoi for file-based DOIs
    },
    [dois, dispatch],
  );

  // reset selection when file changes
  useEffect(() => {
    dispatch(setSelectedFileDoi(null));

    return () => {
      dispatch(setSelectedFileDoi(null));
    };
  }, [dispatch, fileIdToUse]);

  // automatically select the first DOI when data loads or when selected DOI no longer exists
  useEffect(() => {
    if (isSuccess && dois?.results?.length && !isLoading) {
      // check if currently selected DOI exists in the results
      const selectedDoiExists = selectedFileDoi
        ? dois.results.some((doi) => doi.id === selectedFileDoi)
        : false;

      // if no DOI is selected or the selected DOI no longer exists, select the first one
      if (!selectedFileDoi || !selectedDoiExists) {
        selectDoi(dois.results[0].id);
      }
    }
  }, [dois, isSuccess, isLoading, selectedFileDoi, selectDoi]);

  // get a specific DOI by ID directly from the cache
  const getDoiById = useCallback(
    (id: number) => {
      if (!dois?.results) return null;
      return dois.results.find((doi) => doi.id === id) || null;
    },
    [dois],
  );

  // get the selected DOI object from the array using the selectedFileDoi ID
  const selectedDOI =
    selectedFileDoi && dois?.results
      ? dois.results.find((doi) => doi.id === selectedFileDoi) || null
      : null;

  return {
    dois: dois?.results || [],
    selectedDoiId: selectedFileDoi,
    selectedDOI,
    selectDoi,
    getDoiById,
    isLoading,
    isSuccess,
    file,
  };
}
